import React, { useMemo, useState } from "react";
import pluswhite from "assets/images/icon/plus-white.png";
import filtersearch from "assets/images/icon/filter-search.png";
import Table from "components/Table";
import Input from "components/Input";
import AddDepartmentModal from "pages/AddDepartmentModal";
import DepartmentDetailsModal from "pages/DepartmentDetailsModal";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getDepartments, deleteDepartment } from "services/departments";
import { getWorkplaces } from "services/workplaces";

const headers = [
    { headerName: "Department Name", key: "departmentName" },
    { headerName: "Workplace", key: "workplace" },
    { headerName: "Active Status", key: "active_status" },
    { headerName: "", key: "action" },
];

const ManageDepartments = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDept, setSelectedDept] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [defaultEditing, setDefaultEditing] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const queryClient = useQueryClient();

    const { data: departmentsRaw = [], isLoading } = useQuery({ queryKey: ['departments'], queryFn: getDepartments });

    const deleteMutation = useMutation({
        mutationFn: (id) => deleteDepartment(id),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['departments'] }); setDeleteTarget(null); },
    });
    const { data: workplacesRaw = [] } = useQuery({ queryKey: ['workplaces'], queryFn: getWorkplaces });

    const workplaceMap = useMemo(() =>
        Object.fromEntries(workplacesRaw.map(w => [w.id, w.name])),
        [workplacesRaw]);

    const tableData = useMemo(() => departmentsRaw
        .filter(d => !searchTerm || d.name?.toLowerCase().includes(searchTerm.toLowerCase()))
        .map(d => ({
            id: d.id,
            departmentName: d.name,
            workplace: workplaceMap[d.workplaceId] || d.workplaceId || '—',
            active_status: d.active ? 'Active' : 'Inactive',
        })),
        [departmentsRaw, searchTerm, workplaceMap]);

    const handleEdit = (row) => {
        setSelectedDept(departmentsRaw.find(d => d.id === row.id) || row);
        setDefaultEditing(true);
        setDetailsOpen(true);
    };

    const handleView = (row) => {
        setSelectedDept(departmentsRaw.find(d => d.id === row.id) || row);
        setDefaultEditing(false);
        setDetailsOpen(true);
    };

    const handleDelete = (row) => setDeleteTarget(departmentsRaw.find(d => d.id === row.id) || row);

    return (
        <>
            <div>
                <div className='d-flex justify-content-between align-content-end border-bottom pb-4'>
                    <div>
                        <h2 className='sub-title'>Manage Departments</h2>
                        <button className='button-orange py-2 mt-3 rounded-pill d-flex justify-content-center align-items-center gap-2' onClick={() => setIsOpen(true)}>
                            <img src={pluswhite} alt="add" />Add Department
                        </button>
                    </div>
                    <div className='align-content-end'>
                        <Input placeholder='Search by Name' startIcon={filtersearch} style={{ width: '300px' }} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                    </div>
                </div>
                <div>
                    {isLoading ? <p className="text-center mt-4">Loading departments...</p> : (
                        <Table headers={headers} data={tableData} showPagination={true} handleEdit={handleEdit} handleView={handleView} handleDelete={handleDelete} />
                    )}
                </div>
            </div>
            <AddDepartmentModal open={isOpen} onClose={() => { setIsOpen(false); queryClient.invalidateQueries({ queryKey: ['departments'] }); }} />
            <DepartmentDetailsModal
                department={selectedDept}
                open={detailsOpen}
                defaultEditing={defaultEditing}
                onClose={() => { setDetailsOpen(false); setSelectedDept(null); }}
            />
            {deleteTarget && (
                <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
                        <div className="modal-content p-4">
                            <h5 className="mb-2">Delete Department</h5>
                            <p className="text-muted mb-4">Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This action cannot be undone.</p>
                            {deleteMutation.isError && <p className="text-danger mb-3">Failed to delete. Please try again.</p>}
                            <div className="d-flex gap-2 justify-content-end">
                                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>Cancel</button>
                                <button className="btn btn-danger rounded-pill px-4" onClick={() => deleteMutation.mutate(deleteTarget.id)} disabled={deleteMutation.isPending}>
                                    {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ManageDepartments;

